/**
 * Workspace — your saved AI generations, reports, decks & projects in one place.
 *
 * Pulls /api/premium/workspace/items + /stats and renders a tabbed grid.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  FolderKanban, Sparkles, Youtube, FileText, Presentation, Plus,
  ExternalLink, RefreshCw, Search,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, PremiumButton, SectionHeading, EmptyState } from "@/components/premium/Primitives";
import ReportExportMenu from "@/components/premium/ReportExportMenu";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || '';

const TYPE_META = {
  teaching: { label: "Teaching", Icon: Sparkles, color: "#A855F7" },
  channel:  { label: "YouTube",  Icon: Youtube,  color: "#A855F7" },
  audit:    { label: "Audit",    Icon: FileText, color: "#A855F7" },
  ppt:      { label: "PPT",      Icon: Presentation, color: "#A855F7" },
};

const TABS = [
  { key: "all",      label: "All" },
  { key: "teaching", label: "Teaching bundles" },
  { key: "channel",  label: "YT reports" },
  { key: "audit",    label: "SEO audits" },
  { key: "ppt",      label: "PPT decks" },
];

const QUICK_ACTIONS = [
  { label: "New PPT deck",       Icon: Presentation, to: "/tools/ai-slides" },
  { label: "Audit YouTube",      Icon: Youtube,      to: "/youtube-suite/channel-assessment" },
  { label: "Teaching bundle",    Icon: Sparkles,     to: "/teaching/ai-assistant" },
  { label: "Site audit",         Icon: FileText,     to: "/tools/site-audit" },
];

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay: d },
});

export default function PremiumWorkspace() {
  const navigate = useNavigate();
  const [active, setActive] = useState("all");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/premium/workspace/items?type=${active}&limit=80`, { withCredentials: true }),
        axios.get(`${API}/api/premium/workspace/stats`, { withCredentials: true }),
      ]);
      setItems(itemsRes.data?.items || []);
      setCounts(statsRes.data?.counts || {});
    } catch (e) {
      console.error("Workspace load failed:", e);
      toast.error("Could not load workspace");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(true);
  }, [active]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((i) =>
      (i.title || "").toLowerCase().includes(q) ||
      (i.subtitle || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const handleOpen = (item) => {
    if (item.url?.startsWith("/api/")) {
      window.open(`${API}${item.url}`, "_blank", "noopener");
    } else if (item.url) {
      navigate(item.url);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans" data-testid="premium-workspace">
      <PremiumNav />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* Header */}
        <motion.div {...fadeUp(0)} className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <SectionHeading
            eyebrow="Workspace"
            title="Your saved AI work, all in one place"
            subtitle="Every bundle, report, audit and deck you've created — organised and exportable."
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setRefreshing(true); load(false); }}
              className="p-2 rounded-full border border-slate-200 hover:bg-slate-50 transition-colors"
              data-testid="workspace-refresh"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <PremiumButton icon={Plus} onClick={() => navigate("/ai-studio")} data-testid="workspace-new-btn">
              New project
            </PremiumButton>
          </div>
        </motion.div>

        {/* Quick actions strip */}
        <motion.div {...fadeUp(0.05)} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {QUICK_ACTIONS.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              data-testid={`workspace-quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 hover:border-[#A855F7]/40 hover:shadow-[0_4px_16px_rgba(168,85,247,0.08)] transition-all group"
            >
              <span className="w-9 h-9 rounded-xl bg-[#FAF5FF] flex items-center justify-center group-hover:bg-[#A855F7] transition-colors">
                <q.Icon className="w-4 h-4 text-[#A855F7] group-hover:text-white transition-colors" />
              </span>
              <span className="text-sm font-medium text-[#111111] flex-1">{q.label}</span>
              <ExternalLink className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </motion.div>

        {/* Tabs */}
        <motion.div {...fadeUp(0.1)} className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
          {TABS.map((t) => {
            const c = counts[t.key] ?? 0;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`workspace-tab-${t.key}`}
                className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors ${
                  active === t.key ? "text-[#A855F7]" : "text-slate-500 hover:text-[#111111]"
                }`}
              >
                {t.label}
                {c > 0 && (
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
                    active === t.key ? "bg-[#A855F7] text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                    {c}
                  </span>
                )}
                {active === t.key && (
                  <motion.span layoutId="ws-tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#A855F7]" />
                )}
              </button>
            );
          })}
          <div className="ml-auto relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search saved items…"
              className="text-sm pl-9 pr-3 py-2 rounded-full border border-slate-200 focus:outline-none focus:border-[#A855F7] transition-colors w-56"
              data-testid="workspace-search"
            />
          </div>
        </motion.div>

        {/* Items grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-32 rounded-2xl border border-slate-200 bg-slate-50 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <PremiumCard className="p-2" hover={false}>
            <EmptyState
              icon={FolderKanban}
              title="Nothing saved here yet"
              subtitle="Use the quick actions above to create your first item — it'll appear here automatically."
              action={
                <PremiumButton icon={Plus} onClick={() => navigate("/ai-studio")} data-testid="workspace-empty-cta">
                  Create something
                </PremiumButton>
              }
            />
          </PremiumCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="workspace-items">
            {filtered.map((item, i) => {
              const meta = TYPE_META[item.type] || TYPE_META.teaching;
              return (
                <motion.div
                  key={`${item.type}-${item.id}`}
                  {...fadeUp(0.02 * i)}
                  className="group p-5 rounded-2xl border border-slate-200 bg-white hover:border-[#A855F7]/40 hover:shadow-[0_8px_24px_rgba(168,85,247,0.08)] transition-all cursor-pointer flex flex-col"
                  onClick={() => handleOpen(item)}
                  data-testid={`workspace-item-${item.type}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <span className="w-9 h-9 rounded-xl bg-[#FAF5FF] flex items-center justify-center shrink-0">
                      <meta.Icon className="w-4 h-4 text-[#A855F7]" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] tracking-wider font-semibold uppercase text-[#A855F7]">{meta.label}</p>
                      <h3 className="text-sm font-semibold text-[#111111] truncate leading-tight" title={item.title}>{item.title}</h3>
                    </div>
                  </div>
                  {item.subtitle && (
                    <p className="text-xs text-slate-500 mb-3 line-clamp-2">{item.subtitle}</p>
                  )}
                  <p className="text-[11px] text-slate-400 mb-4 mt-auto">
                    {item.created_at ? new Date(item.created_at).toLocaleString(undefined, {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    }) : "—"}
                  </p>
                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                    {item.type === "ppt" ? (
                      <a
                        href={`${API}${item.download_url}`}
                        download
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-[#111111] text-white hover:bg-black transition-colors"
                        data-testid="workspace-item-download"
                      >
                        Download .pptx
                      </a>
                    ) : item.exportable ? (
                      <ReportExportMenu
                        type={item.type === "channel" ? "channel" : "audit"}
                        id={item.id}
                        size="sm"
                      />
                    ) : (
                      <button
                        onClick={() => handleOpen(item)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-slate-100 text-[#111111] hover:bg-slate-200 transition-colors"
                      >
                        Open
                      </button>
                    )}
                    {item.share_url && (
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}${item.share_url}`;
                          navigator.clipboard.writeText(url);
                          toast.success("Share link copied");
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-slate-200 hover:bg-slate-50 transition-colors text-slate-700"
                      >
                        Share
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <PremiumFooter />
    </div>
  );
}
